import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';


interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    id: 1,
    question: 'How do I place an order on Dehleze?',
    answer:
      'To place an order, browse our products, add items to your cart, and proceed to checkout. You can pay securely using various payment methods. Once confirmed, you\'ll receive an order confirmation email with tracking details.',
  },
  {
    id: 2,
    question: 'What payment methods do you accept?',
    answer:
      'We accept credit/debit cards, mobile wallets, bank transfers, and cash on delivery. All transactions are secured with SSL encryption to protect your financial information.',
  },
  {
    id: 3,
    question: 'How long does delivery take?',
    answer:
      'Standard delivery typically takes 3-7 business days depending on your location. Express delivery options are available for faster shipping. You can track your order in real-time using the tracking number provided.',
  },
  {
    id: 4,
    question: 'What is your return policy?',
    answer:
      'We offer a 30-day return policy for most items in original condition. If you\'re not satisfied with your purchase, simply initiate a return request from your account and follow the return instructions. Refunds are processed within 7-10 business days.',
  },
  {
    id: 5,
    question: 'Can I cancel or modify my order?',
    answer:
      'You can cancel or modify your order within 24 hours of placing it. After 24 hours, the order enters processing and cannot be changed. Contact our support team immediately if you need urgent changes.',
  },
  {
    id: 6,
    question: 'Is my personal information safe?',
    answer:
      'Yes, we use advanced encryption and security protocols to protect your personal information. Your data is never shared with third parties without consent. We comply with all international data protection regulations.',
  },
  {
    id: 7,
    question: 'Do you offer international shipping?',
    answer:
      'Currently, we offer shipping within the country. We\'re expanding our international shipping soon. Sign up for our newsletter to be notified when international delivery becomes available.',
  },
  {
    id: 8,
    question: 'How do I track my order?',
    answer:
      'You can track your order using the tracking number sent to your email. Visit our "Track Order" page, enter your order number or email, and you\'ll see real-time updates on your shipment status.',
  },
  {
    id: 9,
    question: 'What should I do if I receive a damaged product?',
    answer:
      'If you receive a damaged product, contact us immediately with photos of the damage. We\'ll arrange a replacement or full refund. Our customer support team is available 24/7 to assist you.',
  },
  {
    id: 10,
    question: 'How can I become a seller on Dehleze?',
    answer:
      'Interested in selling on Dehleze? Visit our "Work With Us" or "Partnerships" page to learn about seller requirements and benefits. Complete the registration form, and our team will review your application.',
  },
  {
    id: 11,
    question: 'Do you have a customer loyalty program?',
    answer:
      'Yes! Sign up for our rewards program and earn points with every purchase. Points can be redeemed for discounts on future orders, exclusive deals, and special offers.',
  },
  {
    id: 12,
    question: 'How do I contact customer support?',
    answer:
      'Our customer support team is available 24/7 via email, phone, and live chat. Visit our "Contact Us" page for all communication channels. Average response time is under 2 hours.',
  },
];

export default function FAQPage() {
  const [openId, setOpenId] = useState<number | null>(1);

  const toggleFAQ = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-[#f5f6fb] flex flex-col">
      <main className="flex-1 w-full max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="text-xs text-gray-500 mb-8">
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
          {' / '}
          <span className="text-gray-700 font-medium">FAQs</span>
        </nav>

        {/* Header Section */}
        <section className="mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Frequently Asked Questions
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Find answers to common questions about shopping, delivery, returns, and more.
          </p>
        </section>

        {/* Search Section */}
      
      
      
      
      
      
      

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
              >
                <span className="font-semibold text-gray-900 text-sm sm:text-base">
                  {faq.question}
                </span>
                <ChevronDown
                  size={20}
                  className={`text-orange-500 flex-shrink-0 transition-transform ${
                    openId === faq.id ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {openId === faq.id && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <section className="mt-12 bg-gradient-to-r from-orange-50 to-pink-50 rounded-2xl p-6 sm:p-8 border border-orange-200">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
            Didn't find your answer?
          </h2>
          <p className="text-gray-600 text-sm sm:text-base mb-4">
            Our customer support team is here to help! Get in touch with us for personalized assistance.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              className="inline-block px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors text-center text-sm sm:text-base"
            >
              Contact Support
            </Link>
            <a
              href="mailto:support@dehleze.com"
              className="inline-block px-6 py-3 bg-white text-orange-500 border border-orange-500 rounded-lg font-semibold hover:bg-orange-50 transition-colors text-center text-sm sm:text-base"
            >
              Email Us
            </a>
          </div>
        </section>
      </main>


    </div>
  );
}
